// Estado de cuenta Fintoc, annotated with what WE know about each movement.
//
// The rows themselves (every movement, Fintoc's running balance, the transfer
// behind it) come from pagos-svc — only it holds the Fintoc key. This module
// adds the business side from Supabase, as the logged-in user:
//
//   inbound  → payments (by external_id = transfer id) → payment_applications
//              → invoices_issued folios / reservations, applied vs. por aplicar
//   outbound → payouts (by fintoc_transfer_id) → the payable: service (provider
//              + its factura), deposit return (reservation), traslado
//              settlement, or a treasury top-up between our own accounts
//
// Nothing here changes a balance or a row: Fintoc's figures are printed as
// Fintoc reported them, and a movement we cannot explain is still listed.
import moment from "moment";
import "moment/locale/es";
import * as XLSX from "xlsx/xlsx.mjs";
import supabase from "../supabase-client";
import { formatPrice } from "./funcs";
import { foliosInConcept, invoiceMatchesConcept } from "./payments";
import { alegraRequest } from "./invoicing";
import { MONEY_FMT, DATE_FMT, applyColumnFormats, toExcelSerial } from "./xlsxExport";
import { NO_INVOICE_LABEL } from "./egresos";

export const ACCOUNTS = [
  { key: "cobranza", label: "Cobranza" },
  { key: "servicios", label: "Servicios / Mantenimientos" },
  { key: "depositos", label: "Depósitos en garantía" },
  { key: "traslados", label: "Traslados" },
  { key: "egresos", label: "Egresos (pagos varios)" },
  { key: "general", label: "General" },
];
export const accountLabel = (key) => ACCOUNTS.find((a) => a.key === key)?.label || key;

const PURPOSE_LABEL = { rent: "Renta", deposit: "Depósito", invoice: "Factura", refund: "Devolución" };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// PostgREST puts .in() lists in the URL; keep each request well under limits.
async function selectIn(table, columns, field, values, extra = (q) => q) {
  const uniq = [...new Set(values.filter((v) => v !== null && v !== undefined && v !== ""))];
  const out = [];
  for (let i = 0; i < uniq.length; i += 100) {
    const { data, error } = await extra(
      supabase.from(table).select(columns).in(field, uniq.slice(i, i + 100))
    );
    if (error) throw new Error(`${table}: ${error.message}`);
    out.push(...(data || []));
  }
  return out;
}

const byId = (rows, key = "id") => Object.fromEntries(rows.map((r) => [String(r[key]), r]));
const customerName = (c) =>
  c ? c.razon_social || [c.first_name, c.last_name].filter(Boolean).join(" ") || `Cliente ${c.id}` : null;

export async function annotateStatement(statement) {
  const rows = statement.rows || [];
  const transferIds = rows.map((r) => r.resource_id).filter(Boolean);

  const [payouts, payments] = await Promise.all([
    selectIn(
      "payouts",
      "id, payable_kind, payable_id, beneficiary_type, beneficiary_id, comment, status, fintoc_transfer_id",
      "fintoc_transfer_id",
      transferIds
    ),
    selectIn(
      "payments",
      "id, direction, payer_type, payer_id, external_id, concept, amount_cents",
      "external_id",
      transferIds,
      (q) => q.eq("source", "fintoc")
    ),
  ]);
  const payoutByTransfer = byId(payouts, "fintoc_transfer_id");
  const paymentByTransfer = byId(payments, "external_id");

  const apps = await selectIn(
    "payment_applications",
    "payment_id, purpose, amount_cents, reservation_id, invoice_id, legacy_transaction_id",
    "payment_id",
    payments.map((p) => p.id)
  );
  // Money applied to a RESERVATION is tied to its facturas one step later:
  // the projected legacy transaction is allocated to invoices (the same link
  // the complemento de pago is stamped from). Following it is what names the
  // factura for most rent payments.
  const allocations = await selectIn(
    "invoice_payment_allocations",
    "transaction_id, invoice_id, amount, rep_uuid",
    "transaction_id",
    apps.map((a) => a.legacy_transaction_id)
  );

  const ofKind = (k) => payouts.filter((p) => p.payable_kind === k).map((p) => p.payable_id);
  const settlements = await selectIn(
    "traslado_settlements",
    "id, traslado_order, trasladista, extra_reason, amount_tickets, amount_chofer, amount_comida, total",
    "id",
    ofKind("traslado_settlement")
  );
  // Egreso payouts pay a frozen request; what it covered is the allocation of
  // the entry it created to cost lines (CFDIs / tickets) of that egreso.
  const egresoRequests = await selectIn(
    "egreso_payment_requests",
    "id, egreso_id, kind, entry_id, description",
    "id",
    ofKind("egreso")
  );
  // Money a tercero returned to their CLABE (Egresos account) lands on their
  // egreso as returned_in / deposit_in entries carrying the payment.
  const egresoReturns = await selectIn(
    "egreso_money_lines",
    "egreso_id, kind, amount_cents, payment_id",
    "payment_id",
    payments.filter((p) => p.payer_type === "payee").map((p) => p.id)
  );
  const egresoIds = [...egresoRequests.map((x) => x.egreso_id), ...egresoReturns.map((x) => x.egreso_id)];
  const [egresos, egresoPayees, egresoAllocs, payees] = await Promise.all([
    selectIn("egresos", "id, title, kind, category, car_id, sublease_plate, reservation_id, no_invoice_reason, no_invoice_note", "id",
      egresoIds),
    selectIn("egreso_payees", "egreso_id, payee_name", "egreso_id", egresoIds),
    selectIn("egreso_allocations", "money_entry_id, cost_entry_id, amount_cents", "money_entry_id",
      egresoRequests.map((x) => x.entry_id)),
    selectIn("payees", "id, name, nickname", "id",
      payments.filter((p) => p.payer_type === "payee").map((p) => p.payer_id)),
  ]);
  const egresoCostLines = await selectIn(
    "egreso_cost_lines",
    "entry_id, cfdi_uuid, serie_folio, emisor_nombre, description, deductible_status, deductible_reason",
    "entry_id",
    egresoAllocs.map((a) => a.cost_entry_id)
  );
  const [services, orders, tickets] = await Promise.all([
    selectIn(
      "services",
      "id, invoice, invoice_id, car, provider, invoice_status, non_deductible_reason",
      "id",
      ofKind("service")
    ),
    selectIn(
      "traslado_orders",
      "id, car, origin_label, dest_label, reservation",
      "id",
      settlements.map((s) => s.traslado_order)
    ),
    // A reembolso pays specific tickets, each approved against the CFDI the
    // merchant (caseta, gasolinera, autobús) issued to GoMove.
    selectIn(
      "traslado_tickets",
      "settlement_id, merchant_name, total, cfdi_uuid, cfdi_pending, ticket_date, deductible, no_cfdi_reason",
      "settlement_id",
      settlements.map((s) => s.id),
      (q) => q.order("ticket_date", { ascending: true })
    ),
  ]);

  const reservationIds = [
    ...apps.map((a) => a.reservation_id),
    ...ofKind("deposit_return"),
    ...ofKind("card_deposit_return"),
    ...orders.map((o) => o.reservation),
  ];
  const reservations = await selectIn("reservations", "id, customer, company, car", "id", reservationIds);

  const verifs = rows.map((r) => r.verification).filter(Boolean);
  const providerIds = [
    ...services.map((s) => s.provider),
    ...verifs.filter((v) => v.owner_type === "service_provider").map((v) => v.owner_id),
  ];
  const trasladistaIds = [
    ...settlements.map((s) => s.trasladista),
    ...verifs.filter((v) => v.owner_type === "trasladista").map((v) => v.owner_id),
  ];
  const customerIds = [
    ...payments.filter((p) => p.payer_type === "customer").map((p) => p.payer_id),
    ...reservations.map((r) => r.customer),
  ];
  const companyIds = [
    ...payments.filter((p) => p.payer_type === "company").map((p) => p.payer_id),
    ...reservations.map((r) => r.company),
  ];

  const [invoices, providers, trasladistas, customers, companies, cars] = await Promise.all([
    selectIn(
      "invoices_issued",
      "id, invoice_number, alegra_id, stamped, cfdi_uuid",
      "id",
      [...apps.map((a) => a.invoice_id), ...allocations.map((x) => x.invoice_id)]
    ),
    selectIn("service_providers", "id, name, rfc", "id", providerIds),
    selectIn("trasladistas", "id, name", "id", trasladistaIds),
    selectIn("customers", "id, first_name, last_name, razon_social", "id", customerIds),
    selectIn("companies", "id, name", "id", companyIds),
    selectIn(
      "cars",
      "id, plate",
      "id",
      [
        ...services.map((s) => s.car),
        ...orders.map((o) => o.car),
        ...reservations.map((r) => r.car),
        ...egresos.map((g) => g.car_id),
      ]
    ),
  ]);

  await fillInvoiceUuids(invoices);

  const maps = {
    apps: apps.reduce((acc, a) => {
      (acc[a.payment_id] ||= []).push(a);
      return acc;
    }, {}),
    invoices: byId(invoices),
    allocations: allocations.reduce((acc, x) => {
      (acc[x.transaction_id] ||= []).push(x);
      return acc;
    }, {}),
    suggestions: {},
    services: byId(services),
    settlements: byId(settlements),
    tickets: tickets.reduce((acc, t) => {
      (acc[t.settlement_id] ||= []).push(t);
      return acc;
    }, {}),
    orders: byId(orders),
    reservations: byId(reservations),
    providers: byId(providers),
    trasladistas: byId(trasladistas),
    customers: byId(customers),
    companies: byId(companies),
    cars: byId(cars),
    egresoRequests: byId(egresoRequests),
    egresos: byId(egresos),
    egresoPayees: Object.fromEntries(egresoPayees.map((x) => [String(x.egreso_id), x.payee_name])),
    egresoAllocs: egresoAllocs.reduce((acc, a) => {
      (acc[a.money_entry_id] ||= []).push(a);
      return acc;
    }, {}),
    payees: byId(payees),
    egresoReturns: egresoReturns.reduce((acc, x) => {
      (acc[x.payment_id] ||= []).push(x);
      return acc;
    }, {}),
    egresoCostLines: Object.fromEntries(egresoCostLines.map((l) => [String(l.entry_id), l])),
  };

  // Payments still por aplicar have no link yet. When the concepto names a
  // folio of that same client, offer it as a SUGGESTION, in its own column —
  // never mixed with the confirmed ones.
  const pending = payments.filter((p) => {
    const applied = (apps || [])
      .filter((a) => a.payment_id === p.id)
      .reduce((s, a) => s + Number(a.amount_cents), 0);
    return p.payer_type && Number(p.amount_cents) - applied > 0 && foliosInConcept(p.concept).length;
  });
  for (const kind of ["company", "customer"]) {
    const ids = pending.filter((p) => p.payer_type === kind).map((p) => p.payer_id);
    if (!ids.length) continue;
    const open = await selectIn(
      "invoices_issued",
      "id, invoice_number, company_id, customer_id",
      kind === "company" ? "company_id" : "customer_id",
      ids,
      (q) => q.neq("status", "cancelled")
    );
    for (const p of pending.filter((x) => x.payer_type === kind)) {
      const folios = foliosInConcept(p.concept);
      const owner = kind === "company" ? "company_id" : "customer_id";
      maps.suggestions[p.id] = open
        .filter((i) => i[owner] === p.payer_id && invoiceMatchesConcept(i.invoice_number, folios))
        .map((i) => i.invoice_number);
    }
  }

  return rows.map((r) => {
    const payout = payoutByTransfer[r.resource_id];
    return {
      ...describe(r, payout, paymentByTransfer[r.resource_id], maps),
      intra: isIntraFintoc(r, payout),
    };
  });
}

// A transfer between two of OUR Fintoc accounts: a treasury top-up, a known
// account CLABE, or GoMove's RFC on both ends at Fintoc itself. GoMove ↔
// Afirme is deliberately NOT one — that money really entered or left Fintoc.
function isIntraFintoc(r, payout) {
  if (r.intra) return true;
  if (payout?.payable_kind === "treasury_topup") return true;
  if (r.own_account) return true;
  const bank = (r.transfer?.counterparty_bank || "").toUpperCase();
  return !!r.internal && bank.includes("FINTOC");
}

// Rows to show/export: everything, or without transfers between our accounts.
export const visibleRows = (statement, annotated, showInternal) =>
  statement.rows
    .map((r, i) => ({ r, a: annotated[i] }))
    .filter(({ a }) => showInternal || !a.intra);

const UUID_ONLY = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The CFDI UUID of our own invoices lives in Alegra (stamp.uuid). Ask once per
// invoice that lacks it and cache it in invoices_issued.cfdi_uuid (write-once,
// guarded in the DB), so the next export reads it locally. A failed lookup
// only leaves the cell blank — it never blocks the statement.
async function fillInvoiceUuids(invoices) {
  const missing = invoices.filter((i) => !i.cfdi_uuid && i.alegra_id && i.stamped !== false);
  for (let k = 0; k < missing.length; k += 4) {
    await Promise.all(
      missing.slice(k, k + 4).map(async (inv) => {
        try {
          const a = await alegraRequest(`invoices/${inv.alegra_id}`, "GET");
          const uuid = String(a?.stamp?.uuid || "").toUpperCase();
          if (!UUID_ONLY.test(uuid)) return;
          inv.cfdi_uuid = uuid;
          await supabase
            .from("invoices_issued")
            .update({ cfdi_uuid: uuid })
            .eq("id", inv.id)
            .is("cfdi_uuid", null);
        } catch {
          // Leave it blank; the next export will try again.
        }
      })
    );
  }
}

const plateOf = (m, carId) => (carId ? m.cars[String(carId)]?.plate || `Auto ${carId}` : "");
const reservationOwner = (m, res) =>
  res
    ? res.company
      ? m.companies[String(res.company)]?.name
      : customerName(m.customers[String(res.customer)])
    : null;

// One movement → the business columns. Every branch fills the same shape.
function describe(r, payout, payment, m) {
  const t = r.transfer || {};
  const d = {
    category: "",
    party: "",
    invoices: "",
    cfdi_uuid: "",
    rep_uuid: "",
    reservations: "",
    service: "",
    traslado: "",
    vehicle: "",
    applied_detail: "",
    suggested: "",
    non_deductible: null,
    non_deductible_reason: "",
    applied: null,
    applied_rent: null,
    applied_deposit: null,
    unapplied: null,
  };

  if (payment && payment.direction === "in" && payment.payer_type === "payee") {
    const t = m.payees[String(payment.payer_id)];
    const lines = (m.egresoReturns[payment.id] || []).filter((x) => Number(x.amount_cents) !== 0);
    const applied = lines.reduce((s, x) => s + Number(x.amount_cents), 0);
    d.category = lines.length && lines.every((x) => x.kind === "deposit_in") ? "Devolución de depósito" : "Regreso de egreso";
    d.party = t?.nickname || t?.name || `Tercero ${payment.payer_id}`;
    d.applied = applied / 100;
    d.unapplied = (Number(payment.amount_cents) - applied) / 100;
    d.applied_detail = lines
      .map((x) => `Egreso ${x.egreso_id}: ${(Number(x.amount_cents) / 100).toFixed(2)}`)
      .join("; ");
    return d;
  }

  if (payment && payment.direction === "in") {
    d.category = "Cobro a cliente";
    d.party =
      payment.payer_type === "company"
        ? m.companies[String(payment.payer_id)]?.name || `Empresa ${payment.payer_id}`
        : payment.payer_type === "customer"
          ? customerName(m.customers[String(payment.payer_id)]) || `Cliente ${payment.payer_id}`
          : "Sin identificar";

    // The ledger is append-only (a reversal is a negative row), so net each
    // target and drop the ones that cancel out.
    const net = {};
    for (const a of m.apps[payment.id] || []) {
      const k = `${a.purpose}|${a.reservation_id || ""}|${a.invoice_id || ""}`;
      net[k] = net[k] || { ...a, amount_cents: 0 };
      net[k].amount_cents += Number(a.amount_cents);
    }
    const live = Object.values(net).filter((a) => a.amount_cents !== 0);
    const applied = live.reduce((s, a) => s + a.amount_cents, 0);
    // Facturas this payment is tied to, and how much went to each: applied to
    // a reservation whose projected transaction was then allocated to a folio,
    // or applied straight to a folio. The allocation is the authoritative
    // amount (it is what the complemento de pago carries); several folio
    // applications share ONE transaction, so each transaction counts once.
    const perInvoice = new Map(); // invoice id → cents
    const txns = new Set((m.apps[payment.id] || []).map((a) => a.legacy_transaction_id).filter(Boolean));
    for (const tx of txns) {
      for (const x of m.allocations[tx] || []) {
        perInvoice.set(x.invoice_id, (perInvoice.get(x.invoice_id) || 0) + Math.round(Number(x.amount) * 100));
      }
    }
    for (const a of live) {
      if (a.invoice_id && !perInvoice.has(a.invoice_id)) perInvoice.set(a.invoice_id, a.amount_cents);
    }
    const folioOf = (id) => m.invoices[String(id)]?.invoice_number || `Factura ${id}`;
    const folios = [...perInvoice.keys()].map(folioOf);
    const resIds = [...new Set(live.map((a) => a.reservation_id).filter(Boolean))];

    // Rent (and folios) is income; a deposit is money held for the client. One
    // transfer can carry both, so the split gets its own summable columns.
    const deposit = live.filter((a) => a.purpose === "deposit").reduce((s, a) => s + a.amount_cents, 0);
    const rent = applied - deposit;

    // With one factura covering the whole payment the folio says it all; when
    // there are several, or a deposit rides along, each folio shows its amount.
    const showAmounts = perInvoice.size > 1 || (deposit > 0 && perInvoice.size > 0) ||
      [...perInvoice.values()].some((c) => c !== Number(payment.amount_cents));
    d.invoices = [...perInvoice.entries()]
      .map(([id, c]) => (showAmounts ? `${folioOf(id)} (${formatPrice(c / 100)})` : folioOf(id)))
      .join(", ");
    // Same order as the folios, so column N of one matches column N of the other.
    const uuids = [...perInvoice.keys()].map((id) => m.invoices[String(id)]?.cfdi_uuid || "—");
    d.cfdi_uuid = uuids.every((u) => u === "—") ? "" : uuids.join(", ");
    d.rep_uuid = [
      ...new Set([...txns].flatMap((tx) => (m.allocations[tx] || []).map((x) => x.rep_uuid)).filter(Boolean)),
    ].join(", ");
    d.reservations = resIds.join(", ");
    d.applied = applied / 100;
    d.applied_rent = rent ? rent / 100 : null;
    d.applied_deposit = deposit ? deposit / 100 : null;
    d.unapplied = (Number(payment.amount_cents) - applied) / 100;
    d.applied_detail = live
      .map((a) => {
        const what =
          a.purpose === "invoice"
            ? m.invoices[String(a.invoice_id)]?.invoice_number || `Factura ${a.invoice_id}`
            : a.purpose === "refund"
            ? `${PURPOSE_LABEL.refund} al remitente`
            : `${PURPOSE_LABEL[a.purpose] || a.purpose} res. ${a.reservation_id}`;
        return `${what}: ${(a.amount_cents / 100).toFixed(2)}`;
      })
      .join("; ");
    if (deposit > 0 && rent > 0) d.category = "Renta + depósito";
    if (live.length && live.every((a) => a.purpose === "deposit")) {
      d.category = "Depósito en garantía";
      // A deposit is not income and is never invoiced; say so rather than
      // leave a blank that reads like a missing factura.
      if (!d.invoices) d.invoices = "Depósito (no se factura)";
    }
    d.suggested = (m.suggestions[payment.id] || []).filter((f) => !folios.includes(f)).join(", ");
    return d;
  }

  if (payout) {
    switch (payout.payable_kind) {
      case "service": {
        const s = m.services[String(payout.payable_id)] || {};
        const p = m.providers[String(s.provider)];
        d.category = "Pago a proveedor";
        d.party = p?.name || (s.provider ? `Proveedor ${s.provider}` : "");
        d.service = payout.payable_id;
        // `invoice_id` is the provider's folio; `invoice` holds the CFDI UUID
        // once the XML is attached (older rows have the folio in both).
        const folio = s.invoice_id && !UUID_RE.test(s.invoice_id) ? s.invoice_id : "";
        d.invoices = folio || (s.invoice && !UUID_RE.test(s.invoice) ? s.invoice : "");
        d.cfdi_uuid = [s.invoice, s.invoice_id].find((v) => v && UUID_RE.test(v)) || "";
        d.vehicle = plateOf(m, s.car);
        // Flagged by a person on the service ("no deducible" + reason): the
        // whole of this payment is outside the deductible expenses.
        if (s.invoice_status === "no_deducible" || s.non_deductible_reason) {
          d.non_deductible = r.amount_cents / 100;
          d.non_deductible_reason = s.non_deductible_reason || "Servicio no deducible";
        }
        break;
      }
      case "deposit_return":
      case "card_deposit_return": {
        const res = m.reservations[String(payout.payable_id)];
        d.category = "Devolución de depósito";
        d.party = reservationOwner(m, res) || "";
        d.reservations = payout.payable_id;
        d.vehicle = plateOf(m, res?.car);
        break;
      }
      case "traslado_settlement": {
        const st = m.settlements[String(payout.payable_id)] || {};
        const o = m.orders[String(st.traslado_order)] || {};
        d.category = st.extra_reason
          ? "Traslado · pago adicional"
          : Number(st.amount_tickets) > 0
            ? "Traslado · reembolso"
            : "Traslado · anticipo";
        d.party = m.trasladistas[String(st.trasladista)]?.name || "";
        d.traslado = st.traslado_order || "";
        d.reservations = o.reservation || "";
        d.vehicle = plateOf(m, o.car);
        d.applied_detail = [o.origin_label, o.dest_label].filter(Boolean).join(" → ");
        // Anticipos and pagos adicionales are chofer pay, not tickets: no CFDI.
        const tks = m.tickets[String(st.id)] || [];
        const noCfdi = (t) => t.deductible === false;
        d.invoices = tks
          .map((t) => {
            const who = t.merchant_name || "Ticket";
            const amt = t.total == null ? "" : ` ${formatPrice(Number(t.total))}`;
            const note = t.cfdi_uuid ? "" : noCfdi(t) ? " (sin CFDI)" : " (CFDI pendiente)";
            return `${who}${amt}${note}`;
          })
          .join("; ");
        d.cfdi_uuid = tks.map((t) => t.cfdi_uuid).filter(Boolean).join(", ");

        // No deducible: what went to the trasladista himself (chofer pay and
        // comida — he issues no CFDI) plus tickets someone marked as having no
        // CFDI. A ticket still WAITING for its CFDI is not counted: that is
        // pending, not decided. Capped at what this transfer actually moved.
        const total = Number(st.total) || 0;
        const driver = Math.max(0, total - (Number(st.amount_tickets) || 0));
        const lost = tks.filter(noCfdi);
        const ndCents = Math.min(
          r.amount_cents,
          Math.round(driver * 100) + lost.reduce((acc, t) => acc + Math.round(Number(t.total || 0) * 100), 0)
        );
        if (ndCents > 0) {
          d.non_deductible = ndCents / 100;
          d.non_deductible_reason = [
            driver > 0 ? (st.extra_reason ? `Pago adicional al trasladista (${st.extra_reason})` : "Pago al trasladista (chofer / comida)") : "",
            ...lost.map((t) => `${t.merchant_name || "Ticket"}: ${t.no_cfdi_reason || "sin CFDI"}`),
          ]
            .filter(Boolean)
            .join("; ");
        }
        break;
      }
      case "egreso": {
        const rq = m.egresoRequests[String(payout.payable_id)] || {};
        const g = m.egresos[String(rq.egreso_id)] || {};
        d.category = rq.kind === "deposit_out" ? "Egreso · depósito en garantía" : `Egreso · ${g.category || g.kind || ""}`.trim();
        d.party = m.egresoPayees[String(rq.egreso_id)] || "";
        d.reservations = g.reservation_id || "";
        d.vehicle = g.car_id ? plateOf(m, g.car_id) : g.sublease_plate || "";
        d.applied_detail = rq.egreso_id ? `Egreso #${rq.egreso_id}${g.title ? ` · ${g.title}` : ""}` : "";
        // Net coverage per cost line (a reversal cancels its original).
        const net = {};
        for (const a of m.egresoAllocs[String(rq.entry_id)] || []) {
          net[a.cost_entry_id] = (net[a.cost_entry_id] || 0) + Number(a.amount_cents);
        }
        const covered = Object.entries(net).filter(([, c]) => c !== 0);
        const lineOf = (id) => m.egresoCostLines[String(id)] || {};
        d.invoices = covered
          .map(([id, c]) => {
            const l = lineOf(id);
            const what = l.cfdi_uuid ? l.serie_folio || l.emisor_nombre || "CFDI" : `Ticket${l.description ? ` ${l.description}` : ""}`;
            return covered.length > 1 ? `${what} (${formatPrice(c / 100)})` : what;
          })
          .join("; ");
        d.cfdi_uuid = covered.map(([id]) => lineOf(id).cfdi_uuid).filter(Boolean).join(", ");
        // A deposit, or money not yet backed by a CFDI, is not an expense yet.
        const uncovered = r.amount_cents - covered.reduce((s2, [, c]) => s2 + c, 0);
        const nd = covered.filter(([id]) => lineOf(id).deductible_status === "no_deducible");
        const ndCents = nd.reduce((s2, [, c]) => s2 + c, 0);
        if (ndCents > 0) {
          d.non_deductible = ndCents / 100;
          d.non_deductible_reason = nd.map(([id]) => lineOf(id).deductible_reason).filter(Boolean).join("; ");
        }
        // An expense that will never have a CFDI: all of it is non-deductible.
        if (g.no_invoice_reason === "no_deducible" && rq.kind !== "deposit_out") {
          d.non_deductible = r.amount_cents / 100;
          d.non_deductible_reason = g.no_invoice_note || "Gasto no deducible";
        }
        if (g.no_invoice_reason) d.invoices = `No lleva factura (${NO_INVOICE_LABEL[g.no_invoice_reason] || g.no_invoice_reason})`;
        else if (rq.kind !== "deposit_out" && uncovered > 0 && !d.invoices) d.invoices = "Por comprobar";
        break;
      }
      case "treasury_topup":
        d.category = `Traspaso a ${r.own_account ? accountLabel(r.own_account) : "cuenta propia"}`;
        d.party = "GOMOVE";
        break;
      default:
        d.category = payout.payable_kind;
    }
    if (payout.status === "returned") d.category += " (devuelto)";
    return d;
  }

  if (r.verification) {
    const v = r.verification;
    d.category = "Verificación de cuenta";
    d.party =
      (v.owner_type === "service_provider" && m.providers[String(v.owner_id)]?.name) ||
      (v.owner_type === "trasladista" && m.trasladistas[String(v.owner_id)]?.name) ||
      v.holder_name ||
      "";
    return d;
  }

  if (r.internal) {
    const where = r.own_account ? accountLabel(r.own_account) : t.counterparty_bank || "cuenta propia";
    d.category = r.direction === "inbound" ? `Traspaso desde ${where}` : `Traspaso a ${where}`;
    d.party = "GOMOVE";
    return d;
  }

  if (r.return_pair_id) {
    d.category = r.direction === "inbound" ? "Devolución SPEI (regresó)" : "Devolución SPEI";
    return d;
  }

  d.category = r.direction === "inbound" ? "Abono sin identificar" : "Cargo sin identificar";
  return d;
}

// ─── Consolidated: all five accounts as one ────────────────────────────────
//
// Fintoc is one bank for us, split into five accounts. Merged, every transfer
// between two of them appears twice — a cargo in one, an abono in the other —
// so it is paired by SPEI clave de rastreo (identical on both halves) and kept
// out of the abonos/cargos totals. Money from or to Afirme is NOT one of those:
// it really entered or left Fintoc, even though it is GoMove on both ends.
//
// Every figure is still Fintoc's: each row keeps its own account's balance,
// and the consolidated balance is the sum of the five as of that movement.
export async function loadConsolidated(getStatement, from, to) {
  // An account not configured yet on the server (e.g. egresos, added after
  // the other five) is left out instead of failing the whole view.
  const statements = (
    await Promise.all(
      ACCOUNTS.map((a) =>
        getStatement(a.key, from, to).catch((err) => {
          if (err?.code === "account_not_configured") return null;
          throw err;
        })
      )
    )
  ).filter(Boolean);

  // Each account's rows arrive already in balance-chain order (pagos-svc
  // resolves same-second ties). Merging by date alone would scramble those
  // ties again, so a tie keeps each account's own sequence.
  const rows = statements
    .flatMap((st) => st.rows.map((r, seq) => ({ ...r, account: st.account.key, seq })))
    .sort((a, b) =>
      a.transaction_date !== b.transaction_date
        ? a.transaction_date < b.transaction_date ? -1 : 1
        : a.account === b.account ? a.seq - b.seq : 0
    );

  // Pair internal transfers: same tracking key, out of one of our accounts
  // and into another, both inside the period.
  const byKey = {};
  for (const r of rows) {
    const tk = r.transfer?.tracking_key;
    if (!tk) continue;
    (byKey[tk] ||= []).push(r);
  }
  for (const group of Object.values(byKey)) {
    const out = group.find((r) => r.direction === "outbound");
    const inn = group.find((r) => r.direction === "inbound" && r.account !== out?.account);
    if (!out || !inn) continue;
    out.intra = true;
    out.intra_peer = inn.account;
    inn.intra = true;
    inn.intra_peer = out.account;
  }

  // Running consolidated balance: each account starts at its opening balance
  // and moves to Fintoc's figure after each of its own movements.
  const last = Object.fromEntries(statements.map((st) => [st.account.key, st.summary.opening_cents ?? 0]));
  const sum = () => Object.values(last).reduce((a, b) => a + b, 0);
  for (const r of rows) {
    if (r.balance_cents != null) last[r.account] = r.balance_cents;
    r.consolidated_balance_cents = sum();
  }

  let extIn = 0;
  let extOut = 0;
  let intra = 0;
  for (const r of rows) {
    if (r.intra) {
      if (r.direction === "outbound") intra += r.amount_cents;
    } else if (r.signed_cents > 0) extIn += r.signed_cents;
    else extOut += -r.signed_cents;
  }
  const opening = statements.reduce((s, st) => s + (st.summary.opening_cents ?? 0), 0);
  const closing = statements.reduce((s, st) => s + (st.summary.closing_cents ?? 0), 0);
  // Paired transfers net to zero; anything unpaired is counted as external on
  // its own side, so this identity holds either way.
  const intraNet = rows.filter((r) => r.intra).reduce((s, r) => s + r.signed_cents, 0);

  const statement = {
    consolidated: true,
    account: { key: "all", clabe: null },
    from,
    to,
    accounts: statements.map((st) => ({ key: st.account.key, clabe: st.account.clabe, summary: st.summary })),
    summary: {
      opening_cents: opening,
      inbound_cents: extIn,
      outbound_cents: extOut,
      intra_cents: intra,
      closing_cents: closing,
      count: rows.length,
      balanced:
        statements.every((st) => st.summary.balanced !== false) &&
        opening + extIn - extOut + intraNet === closing,
    },
    rows,
  };

  const annotated = await annotateStatement(statement);
  rows.forEach((r, i) => {
    if (!r.intra) return;
    annotated[i] = {
      ...annotated[i],
      category:
        r.direction === "outbound"
          ? `Traspaso a ${accountLabel(r.intra_peer)}`
          : `Traspaso desde ${accountLabel(r.intra_peer)}`,
      party: "GOMOVE",
      intra: true,
    };
  });
  return { statement, annotated };
}

// "2026-10-07T00:34:07Z" → Monterrey wall clock (UTC-6, no DST since 2022).
const mtyLocal = (iso) => moment.utc(iso).utcOffset(-360).format("YYYY-MM-DDTHH:mm:ss");
export const mtyDisplay = (iso) => moment.utc(iso).utcOffset(-360).format("DD/MM/YYYY HH:mm");

const HEADERS = [
  "Fecha", "Cargo", "Abono", "Saldo", "Categoría", "Cliente / Beneficiario",
  "Factura(s)", "Factura sugerida (concepto)", "UUID CFDI", "UUID complemento de pago", "Reservación(es)", "Servicio", "Traslado", "Vehículo",
  "Aplicado", "Aplicado a renta / facturas", "Aplicado a depósito", "Por aplicar",
  "No deducible", "Motivo no deducible", "Detalle", "Contraparte (banco)", "RFC contraparte",
  "Banco", "Cuenta contraparte", "Concepto", "Clave de rastreo", "Estado SPEI",
  "CEP", "ID transferencia Fintoc",
];
// Consolidated only: which account, its own balance, the five-account total,
// and a filterable flag to hide transfers between our own accounts.
const CONSOLIDATED_HEADERS = ["Cuenta", "Traspaso interno", "Saldo consolidado"];
const MONEY_COLS = [
  "Cargo", "Abono", "Saldo", "Saldo consolidado", "Aplicado",
  "Aplicado a renta / facturas", "Aplicado a depósito", "Por aplicar", "No deducible",
];
const WIDTHS = {
  Fecha: 16, Cuenta: 24, "Factura sugerida (concepto)": 22, Categoría: 28, "Cliente / Beneficiario": 30, Concepto: 34, Detalle: 34,
  "UUID CFDI": 38, "UUID complemento de pago": 38, "Motivo no deducible": 40, "Contraparte (banco)": 30, CEP: 20, "ID transferencia Fintoc": 30,
};

const cents = (v) => (v == null ? null : v / 100);
const moneyCells = (ws, cells) => {
  for (const [r, c] of cells) {
    const cell = ws[XLSX.utils.encode_cell({ c, r })];
    if (cell && cell.t === "n") cell.z = MONEY_FMT;
  }
};

export function downloadStatementXlsx(statement, annotated, { showInternal = true } = {}) {
  const { account, from, to, summary, consolidated } = statement;
  const shown = visibleRows(statement, annotated, showInternal);
  const label = consolidated ? "Consolidado" : accountLabel(account.key);
  const headers = consolidated
    ? [HEADERS[0], CONSOLIDATED_HEADERS[0], ...HEADERS.slice(1, 4), CONSOLIDATED_HEADERS[2],
       CONSOLIDATED_HEADERS[1], ...HEADERS.slice(4)]
    : HEADERS;

  const body = shown.map(({ r, a }) => {
    const t = r.transfer || {};
    const head = [
      r.signed_cents < 0 ? -r.signed_cents / 100 : null,
      r.signed_cents > 0 ? r.signed_cents / 100 : null,
      cents(r.balance_cents),
    ];
    const date = toExcelSerial(mtyLocal(r.transaction_date));
    return [
      ...(consolidated
        ? [date, accountLabel(r.account), ...head, cents(r.consolidated_balance_cents), r.intra ? "Sí" : "No"]
        : [date, ...head]),
      a.category,
      a.party,
      a.invoices,
      a.suggested,
      a.cfdi_uuid,
      a.rep_uuid,
      a.reservations === "" ? "" : String(a.reservations),
      a.service,
      a.traslado,
      a.vehicle,
      a.applied,
      a.applied_rent,
      a.applied_deposit,
      a.unapplied,
      a.non_deductible,
      a.non_deductible_reason,
      a.applied_detail,
      t.counterparty_name || "",
      t.counterparty_rfc || "",
      t.counterparty_bank || "",
      t.counterparty_account || "",
      t.comment || "",
      t.tracking_key || "",
      t.status || "",
      t.receipt_url || "",
      r.resource_id || "",
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...body]);
  applyColumnFormats(ws, {
    0: DATE_FMT,
    ...Object.fromEntries(MONEY_COLS.filter((h) => headers.includes(h)).map((h) => [headers.indexOf(h), MONEY_FMT])),
  });
  ws["!autofilter"] = { ref: ws["!ref"] };
  ws["!cols"] = headers.map((h) => ({ wch: WIDTHS[h] || Math.max(12, h.length + 2) }));

  // Resumen: Fintoc's own opening/closing figures and the totals per category.
  // Consolidated, transfers between our accounts are left out of the category
  // table — they are listed on their own line instead.
  const byCat = {};
  statement.rows.forEach((r, i) => {
    // Consolidated, paired transfers net to zero and get their own line.
    if (consolidated && r.intra) return;
    const c = (byCat[annotated[i].category] ||= { n: 0, in: 0, out: 0 });
    c.n += 1;
    if (r.signed_cents > 0) c.in += r.signed_cents;
    else c.out += -r.signed_cents;
  });
  const resumen = [
    ["Estado de cuenta Fintoc"],
    ["Cuenta", consolidated ? "Todas las cuentas (consolidado)" : label],
    ["CLABE", account.clabe || ""],
    ["Periodo", `${moment(from).format("DD/MM/YYYY")} – ${moment(to).format("DD/MM/YYYY")}`],
    ["Generado", moment().format("DD/MM/YYYY HH:mm")],
    [],
  ];
  const money = [];
  const push = (row, moneyCols = []) => {
    moneyCols.forEach((c) => money.push([resumen.length, c]));
    resumen.push(row);
  };

  push(["Saldo inicial", cents(summary.opening_cents)], [1]);
  push([consolidated ? "Abonos (externos)" : "Abonos", cents(summary.inbound_cents)], [1]);
  push([consolidated ? "Cargos (externos)" : "Cargos", cents(summary.outbound_cents)], [1]);
  if (consolidated) push(["Traspasos entre cuentas", cents(summary.intra_cents)], [1]);
  push(["Saldo final", cents(summary.closing_cents)], [1]);
  push(["Movimientos", summary.count]);
  // Of the cobros: what was income (renta / facturas) vs. deposits held.
  const sumOf = (k) => annotated.reduce((acc, a) => acc + Math.round((a[k] || 0) * 100), 0);
  push(["Cobros aplicados a renta / facturas", sumOf("applied_rent") / 100], [1]);
  push(["Depósitos en garantía recibidos", sumOf("applied_deposit") / 100], [1]);
  push(["Cobros por aplicar", sumOf("unapplied") / 100], [1]);
  push(["Gastos no deducibles", sumOf("non_deductible") / 100], [1]);
  push([
    "Transferencias entre cuentas Fintoc",
    showInternal ? "Incluidas en Movimientos" : "Ocultas en Movimientos (sí cuentan en saldos)",
  ]);

  if (consolidated) {
    resumen.push([]);
    push(["Cuenta", "CLABE", "Saldo inicial", "Abonos", "Cargos", "Saldo final"]);
    for (const a of statement.accounts) {
      push(
        [accountLabel(a.key), a.clabe || "", cents(a.summary.opening_cents), cents(a.summary.inbound_cents),
         cents(a.summary.outbound_cents), cents(a.summary.closing_cents)],
        [2, 3, 4, 5]
      );
    }
  }

  resumen.push([]);
  push(["Categoría", "Movimientos", "Abonos", "Cargos"]);
  Object.entries(byCat)
    .sort((a, b) => b[1].in + b[1].out - (a[1].in + a[1].out))
    .forEach(([k, v]) => push([k, v.n, v.in / 100, v.out / 100], [2, 3]));

  const ws2 = XLSX.utils.aoa_to_sheet(resumen);
  moneyCells(ws2, money);
  ws2["!cols"] = [{ wch: 30 }, { wch: 22 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws2, "Resumen");
  XLSX.utils.book_append_sheet(wb, ws, "Movimientos");
  XLSX.writeFile(wb, `Estado de cuenta Fintoc - ${label} - ${from} a ${to}.xlsx`);
}
